import { memo, type FC } from "react";
import { Check } from "lucide-react";
import type { PromptAnalysis, PromptClarification } from "../../SegmentPrompt.Mocks";
import { useSegmentFormatters, useSegmentLabel } from "../../UseSegmentLabel";

const PROMPT_EXAMPLE_KEYS = ["highSpendersKarachi", "notOrdered", "churnThisYear"] as const;

const getHintKey = (hasIssues: boolean, hasSuggestion: boolean, hasMatches: boolean) => {
  if (hasIssues) {
    return "prompt.fixRest";
  }
  if (hasSuggestion) {
    return "prompt.addFrequency";
  }
  return hasMatches ? "prompt.readyToFill" : "prompt.noMatches";
};

interface PromptFeedbackProps {
  text: string;
  analysis: PromptAnalysis;
  hasIssues: boolean;
  filledCount: number | null;
  onTry: (_text: string) => void;
  onUndo: () => void;
  onResolveClarification: (_clarification: PromptClarification, _replacement: string) => void;
}

const PromptFeedback: FC<PromptFeedbackProps> = ({
  text,
  analysis,
  hasIssues,
  filledCount,
  onTry,
  onUndo,
  onResolveClarification,
}) => {
  const label = useSegmentLabel();
  const { formatValue } = useSegmentFormatters();
  const { matchLabels, suggestion, clarifications, issues } = analysis;

  if (!text.trim()) {
    return (
      <div className="segment-prompt__status">
        <span className="segment-prompt__muted">{label("prompt.try")}</span>
        {PROMPT_EXAMPLE_KEYS.map(key => {
          const example = label(`prompt.examples.${key}`);
          return (
            <button key={key} type="button" className="segment-prompt__example" onClick={() => onTry(example)}>
              {example}
            </button>
          );
        })}
      </div>
    );
  }

  if (filledCount !== null) {
    return (
      <div className="segment-prompt__status">
        <span className="segment-prompt__matched">
          <Check size={14} aria-hidden />
          {label("prompt.filled", { count: filledCount, value: formatValue(filledCount) })}
        </span>
        <span className="segment-prompt__muted">{label("prompt.checkBelow")}</span>
        <button type="button" className="segment-prompt__undo" onClick={onUndo}>
          {label("prompt.undo")}
        </button>
      </div>
    );
  }

  const hintKey = getHintKey(hasIssues, Boolean(suggestion), matchLabels.length > 0);

  return (
    <>
      <div className="segment-prompt__status">
        {matchLabels.map((matchLabel, index) => (
          <span key={`${matchLabel}-${index}`} className="segment-prompt__matched">
            <Check size={14} aria-hidden />
            {matchLabel}
          </span>
        ))}
        <span className="segment-prompt__muted">{label(hintKey)}</span>
        {suggestion && !hasIssues && (
          <>
            <kbd className="segment-prompt__key">{label("prompt.tabKey")}</kbd>
            <span className="segment-prompt__key-hint">{label("prompt.toAccept")}</span>
          </>
        )}
      </div>

      {clarifications.map(clarification => (
        <div key={clarification.start} className="segment-prompt__notice segment-prompt__notice--clarify">
          <span>{label("prompt.clarify.lead")}</span>
          <strong>“{clarification.phrase}”</strong>
          <span>{label("prompt.clarify.question")}</span>
          {clarification.options.map((option, index) => (
            <span key={option.key} className="segment-prompt__notice-option">
              {index > 0 && <span>{label("prompt.clarify.or")}</span>}
              <button
                type="button"
                className="segment-prompt__choice"
                onClick={() => onResolveClarification(clarification, option.replacement)}
              >
                {label(`prompt.clarify.options.${option.key}`)}
              </button>
            </span>
          ))}
          <span>{label("prompt.clarify.end")}</span>
        </div>
      ))}

      {issues.map((issue, index) => (
        <p key={`${issue.phrase}-${index}`} role="alert" className="segment-prompt__notice segment-prompt__notice--error">
          {label(`prompt.issues.${issue.reason}`, { phrase: issue.phrase })}
        </p>
      ))}
    </>
  );
};

export default memo(PromptFeedback);
